import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import type { CSSProperties } from "react";
import { useId } from "react";

import { cn } from "@/shadcn/lib/utils";

type DragItem = {
  id: string;
  isFixed?: boolean;
};

type ReorderableListProps<T extends DragItem> = {
  items: T[];
  /** Lay the items out in a row instead of a column (e.g. an image filmstrip). */
  direction?: "vertical" | "horizontal";
  className?: string;
  onReorder: (items: T[]) => void;
  renderListItem: (item: T, index: number) => React.ReactNode;
};

export const ReorderableList = <T extends DragItem>({
  items,
  direction = "vertical",
  className,
  onReorder,
  renderListItem,
}: ReorderableListProps<T>) => {
  const droppableId = useId();

  const handleOnDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const itemsArray = Array.from(items);
    const [reorderedItem] = itemsArray.splice(result.source.index, 1);
    itemsArray.splice(result.destination.index, 0, reorderedItem);

    onReorder(itemsArray);
  };

  return (
    <DragDropContext onDragEnd={handleOnDragEnd}>
      <Droppable direction={direction} droppableId={droppableId}>
        {(provided, snapshot) => (
          <ul
            {...provided.droppableProps}
            className={cn(
              direction === "horizontal" && "flex",
              snapshot.isDraggingOver && "bg-gray-50",
              className,
            )}
            ref={provided.innerRef}
          >
            {items.map((item, index) => (
              <Draggable
                draggableId={item.id}
                index={index}
                isDragDisabled={item.isFixed}
                key={item.id}
              >
                {(provided) => {
                  const { style, ...draggableProps } = provided.draggableProps;

                  return (
                    <li
                      {...draggableProps}
                      {...provided.dragHandleProps}
                      ref={provided.innerRef}
                      style={style as CSSProperties | undefined}
                    >
                      {renderListItem(item, index)}
                    </li>
                  );
                }}
              </Draggable>
            ))}
            {provided.placeholder}
          </ul>
        )}
      </Droppable>
    </DragDropContext>
  );
};
